/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CallbackRuleSettingsSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/


import { Phone, Settings } from 'lucide-react';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import RecordingSelect from '../../../components/ui/RecordingSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { MultiSelectButton } from '../../../components/ui/MultiSelectButton';
import { MultiSelectModal } from '../../../components/ui/MultiSelectModal';

interface NotificationSettings {
  method: 'none' | 'email' | 'api' | 'event';
  target: string;
}

interface FormSettings {
  delaySeconds: number;
  dialPrefix: string;
  retryCount: number;
  maxWaitTime: number;
  recordCall: boolean;
  hangupAfterCallback: boolean;
  announcementPath: string;
  ringbackTone: 'us-ring' | 'uk-ring' | 'fr-ring' | 'none';
  originValidationMode: 'none' | 'caller_id' | 'cos' | 'list';
  allowedCallers: string[];
  callbackStrategy: 'immediate' | 'scheduled' | 'queue';
  notification: NotificationSettings;
}

const RINGBACK_TONES: Array<FormSettings['ringbackTone']> = ['us-ring', 'uk-ring', 'fr-ring', 'none'];
const ORIGIN_VALIDATION_MODES: Array<FormSettings['originValidationMode']> = ['none', 'caller_id', 'cos', 'list'];
const CALLBACK_STRATEGIES: Array<FormSettings['callbackStrategy']> = ['immediate', 'scheduled', 'queue'];

interface CallbackRuleSettingsSectionProps {
  settings: FormSettings;
  errors: Record<string, string>;
  availableExtensions: Array<{ id: string; name: string; description: string; slug: string }>;
  showAllowedCallersModal: boolean;
  setShowAllowedCallersModal: (open: boolean) => void;
  onSettingsChange: <K extends keyof FormSettings>(key: K, value: FormSettings[K]) => void;
  onNotificationChange: (key: keyof NotificationSettings, value: string) => void;
  t: (key: string, fallback?: string) => string;
}

export function CallbackRuleSettingsSection({
  settings, errors, availableExtensions,
  showAllowedCallersModal, setShowAllowedCallersModal,
  onSettingsChange, onNotificationChange, t,
}: CallbackRuleSettingsSectionProps) {
  return (
    <>
      <FormBox title={t('callbackRules:sections.settings')} icon={<Settings className="h-5 w-5" />} className="mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">

          {/* Delay Seconds */}
          <Label htmlFor="delaySeconds">
            <SimpleTooltip content={t('callbackRules:fields.delaySeconds.tooltip')}>
              {t('callbackRules:fields.delaySeconds.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Input id="delaySeconds" type="number" min={0} max={600} value={settings.delaySeconds}
              onChange={(e) => onSettingsChange('delaySeconds', Number(e.target.value) || 0)}
              className={errors.delaySeconds ? 'border-red-500' : ''} />
            {errors.delaySeconds && <p className="text-sm text-red-500 mt-1">{errors.delaySeconds}</p>}
          </div>

          {/* Dial Prefix */}
          <Label htmlFor="dialPrefix">
            <SimpleTooltip content={t('callbackRules:fields.dialPrefix.tooltip')}>
              {t('callbackRules:fields.dialPrefix.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Input id="dialPrefix" value={settings.dialPrefix}
              onChange={(e) => onSettingsChange('dialPrefix', e.target.value)}
              placeholder={t('callbackRules:fields.dialPrefix.placeholder')} />
          </div>

          {/* Retry Count */}
          <Label htmlFor="retryCount">
            <SimpleTooltip content={t('callbackRules:fields.retryCount.tooltip')}>
              {t('callbackRules:fields.retryCount.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Input id="retryCount" type="number" min={0} max={5} value={settings.retryCount}
              onChange={(e) => onSettingsChange('retryCount', Number(e.target.value) || 0)}
              className={errors.retryCount ? 'border-red-500' : ''} />
            {errors.retryCount && <p className="text-sm text-red-500 mt-1">{errors.retryCount}</p>}
          </div>

          {/* Max Wait Time */}
          <Label htmlFor="maxWaitTime">
            <SimpleTooltip content={t('callbackRules:fields.maxWaitTime.tooltip')}>
              {t('callbackRules:fields.maxWaitTime.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Input id="maxWaitTime" type="number" min={0} max={120} value={settings.maxWaitTime}
              onChange={(e) => onSettingsChange('maxWaitTime', Number(e.target.value) || 0)}
              className={errors.maxWaitTime ? 'border-red-500' : ''} />
            {errors.maxWaitTime && <p className="text-sm text-red-500 mt-1">{errors.maxWaitTime}</p>}
          </div>

          {/* Record Call */}
          <Label>
            <SimpleTooltip content={t('callbackRules:fields.recordCall.tooltip')}>
              {t('callbackRules:fields.recordCall.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Toggle value={settings.recordCall} onChange={(v) => onSettingsChange('recordCall', v)} />
          </div>

          {/* Hangup After Callback */}
          <Label>
            <SimpleTooltip content={t('callbackRules:fields.hangupAfterCallback.tooltip')}>
              {t('callbackRules:fields.hangupAfterCallback.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Toggle value={settings.hangupAfterCallback} onChange={(v) => onSettingsChange('hangupAfterCallback', v)} />
          </div>

          {/* Announcement Path */}
          <Label htmlFor="announcementPath">
            <SimpleTooltip content={t('callbackRules:fields.announcementPath.tooltip')}>
              {t('callbackRules:fields.announcementPath.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <RecordingSelect value={settings.announcementPath}
              onChange={(v) => onSettingsChange('announcementPath', v || '')}
              placeholder={t('callbackRules:fields.announcementPath.placeholder')}
              returnType="id" showPlayButton />
          </div>

          {/* Ringback Tone */}
          <Label htmlFor="ringbackTone">
            <SimpleTooltip content={t('callbackRules:fields.ringbackTone.tooltip')}>
              {t('callbackRules:fields.ringbackTone.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Select value={settings.ringbackTone} onValueChange={(v) => onSettingsChange('ringbackTone', v as FormSettings['ringbackTone'])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RINGBACK_TONES.map((tone) => (
                  <SelectItem key={tone} value={tone}>{t(`callbackRules:fields.ringbackTone.options.${tone}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Origin Validation Mode */}
          <Label htmlFor="originValidationMode">
            <SimpleTooltip content={t('callbackRules:fields.originValidationMode.tooltip')}>
              {t('callbackRules:fields.originValidationMode.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Select value={settings.originValidationMode} onValueChange={(v) => onSettingsChange('originValidationMode', v as FormSettings['originValidationMode'])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ORIGIN_VALIDATION_MODES.map((mode) => (
                  <SelectItem key={mode} value={mode}>{t(`callbackRules:fields.originValidationMode.options.${mode}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Allowed Callers (list mode only) */}
          {settings.originValidationMode === 'list' && (
            <div className="col-span-4">
              <MultiSelectButton
                items={availableExtensions}
                selectedIds={settings.allowedCallers}
                onClick={() => setShowAllowedCallersModal(true)}
                label={t('callbackRules:fields.allowedCallers.label')}
                placeholder={t('callbackRules:fields.allowedCallers.placeholder')}
                icon={Phone}
                maxPreviewItems={3}
              />
            </div>
          )}

          {/* Callback Strategy */}
          <Label htmlFor="callbackStrategy">
            <SimpleTooltip content={t('callbackRules:fields.callbackStrategy.tooltip')}>
              {t('callbackRules:fields.callbackStrategy.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Select value={settings.callbackStrategy} onValueChange={(v) => onSettingsChange('callbackStrategy', v as FormSettings['callbackStrategy'])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CALLBACK_STRATEGIES.map((strategy) => (
                  <SelectItem key={strategy} value={strategy}>{t(`callbackRules:fields.callbackStrategy.options.${strategy}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notification Method */}
          <Label htmlFor="notificationMethod">
            <SimpleTooltip content={t('callbackRules:fields.notification.tooltip')}>
              {t('callbackRules:fields.notification.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Select value={settings.notification.method} onValueChange={(v) => onNotificationChange('method', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('callbackRules:fields.notification.options.none')}</SelectItem>
                <SelectItem value="email">{t('callbackRules:fields.notification.options.email')}</SelectItem>
                <SelectItem value="api">{t('callbackRules:fields.notification.options.api')}</SelectItem>
                <SelectItem value="event">{t('callbackRules:fields.notification.options.event')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notification Target (conditional) */}
          {settings.notification.method !== 'none' && (
            <>
              <Label htmlFor="notificationTarget">
                <SimpleTooltip content={t('callbackRules:fields.notification.target.tooltip')}>
                  <span>
                    {t('callbackRules:fields.notification.target.label')}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </SimpleTooltip>
              </Label>
              <div className="col-span-3">
                <Input id="notificationTarget" value={settings.notification.target}
                  onChange={(e) => onNotificationChange('target', e.target.value)}
                  placeholder={
                    settings.notification.method === 'email'
                      ? t('callbackRules:fields.notification.target.placeholder.email')
                      : settings.notification.method === 'api'
                        ? t('callbackRules:fields.notification.target.placeholder.api')
                        : t('callbackRules:fields.notification.target.placeholder.event')
                  }
                  className={errors.notification_target ? 'border-red-500' : ''} />
                {errors.notification_target && <p className="text-sm text-red-500 mt-1">{errors.notification_target}</p>}
              </div>
            </>
          )}
        </div>
      </FormBox>

      {/* Allowed Callers Multi-Select Modal */}
      <MultiSelectModal
        isOpen={showAllowedCallersModal}
        onClose={() => setShowAllowedCallersModal(false)}
        title={t('callbackRules:fields.allowedCallers.modalTitle')}
        description={t('callbackRules:fields.allowedCallers.modalDescription')}
        items={availableExtensions}
        selectedIds={settings.allowedCallers}
        onSelectionChange={(selected) => onSettingsChange('allowedCallers', selected)}
        searchPlaceholder={t('callbackRules:fields.allowedCallers.searchPlaceholder')}
        icon={Phone}
      />
    </>
  );
}
